import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { validateEmail, validatePassword } from "../../utils/validation";

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Validar email
    if (!email) {
      errors.email = "Email é obrigatório";
    } else if (!validateEmail(email)) {
      errors.email = "Email inválido";
    }

    // Validar senha
    if (!password) {
      errors.password = "Senha é obrigatória";
    } else if (!isLogin) {
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        errors.password = passwordValidation.errors[0].message;
      }
    }

    // Validar nome (apenas no registro)
    if (!isLogin && !name) {
      errors.name = "Nome é obrigatório";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setFieldErrors({});

    // Validar formulário
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        await signIn(email, password);
        setSuccess("Login realizado com sucesso! Redirecionando...");
        setTimeout(() => {
          navigate("/dashboard");
        }, 1000);
      } else {
        await signUp(email, password, name);
        setSuccess("Conta criada com sucesso! Verifique seu email para confirmar.");
        setTimeout(() => {
          setIsLogin(true);
        }, 2000);
      }
    } catch (err: any) {
      console.error("Erro na autenticação:", err);
      
      // Mensagens de erro específicas
      if (err.message?.includes("Invalid login credentials")) {
        setError("Email ou senha incorretos");
      } else if (err.message?.includes("Email not confirmed")) {
        setError("Por favor, confirme seu email antes de fazer login");
      } else if (err.message?.includes("User already registered")) {
        setError("Este email já está cadastrado");
      } else if (err.message?.includes("Signups not allowed")) {
        setError("Registros não estão habilitados. Entre em contato com o administrador.");
      } else {
        setError(err.message || "Erro ao processar sua solicitação");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo e Título */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-purple-600 rounded-2xl mb-4">
            <i className="ri-community-line text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Sistema de Gestão Comunitária
          </h1>
          <p className="text-gray-600">
            {isLogin ? "Entre na sua conta" : "Crie sua conta"}
          </p>
        </div>

        {/* Card de Login/Registro */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Mensagens de Erro/Sucesso */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">Erro</p>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-start gap-3">
              <i className="ri-checkbox-circle-fill text-green-500 text-xl flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">Sucesso</p>
                <p className="text-sm text-green-700 mt-1">{success}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Nome (apenas no registro) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome Completo
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                    fieldErrors.name ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Seu nome completo"
                  disabled={loading}
                />
                {fieldErrors.name && (
                  <p className="mt-1 text-sm text-red-600">{fieldErrors.name}</p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                  fieldErrors.email ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="seu@email.com"
                disabled={loading}
              />
              {fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.email}</p>
              )}
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Senha
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all ${
                  fieldErrors.password ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="••••••••"
                disabled={loading}
              />
              {fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.password}</p>
              )}
              {!isLogin && !fieldErrors.password && (
                <p className="mt-1 text-xs text-gray-500">
                  Mínimo 8 caracteres, incluindo maiúsculas, minúsculas e números
                </p>
              )}
            </div>

            {/* Botão de Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-500 to-purple-600 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  {isLogin ? (
                    <>
                      <i className="ri-login-box-line" />
                      Entrar
                    </>
                  ) : (
                    <>
                      <i className="ri-user-add-line" />
                      Criar Conta
                    </>
                  )}
                </>
              )}
            </button>
          </form>

          {/* Toggle Login/Registro */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError("");
                setSuccess("");
                setFieldErrors({});
              }}
              className="text-teal-600 hover:text-teal-700 font-medium transition-colors"
              disabled={loading}
            >
              {isLogin
                ? "Não tem uma conta? Cadastre-se"
                : "Já tem uma conta? Faça login"}
            </button>
          </div>
        </div>

        {/* Informações Adicionais */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Sistema seguro e protegido</p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <span className="flex items-center gap-1">
              <i className="ri-shield-check-line text-teal-600" />
              Criptografia
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-lock-line text-teal-600" />
              LGPD
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
